import { useDisclosure } from '@chakra-ui/react';
import { pathDict } from '@ui/config/paths';
import { useRealPathname } from '@ui/hooks/router';
import { useBalance } from '@ui/hooks/useBalance';
import { currentWallet } from '@veno-app/wallet';
import { useRef, useState } from 'react';
import { useInterval, useUnmount } from 'react-use';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useTransakModal } from '../TransakModal';

const MIN_CRO = 10;
const WAIT_MICROSECONDS = 10_000;
const SHOW_AGAIN_MICROSECONDS = 30 * 60_000;

type UseLowCroBalanceDrawerStore = {
  // in microseconds
  lastShownTime: number | null;
};

const useLowCroBalanceDrawer = create(
  persist<UseLowCroBalanceDrawerStore>(
    () => ({
      lastShownTime: null,
    }),
    {
      name: 'useLowCroBalanceDrawer',
    },
  ),
);

export const useLowCroBalanceDrawerDisclosure = () => {
  const disclosure = useDisclosure();
  const balanceRet = useBalance();
  const realPathname = useRealPathname();
  const isConnected = currentWallet.useIsConnected();
  const isConnectedToSupportedChain =
    currentWallet.useIsConnectedToSupportedChain();

  const isShownRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const [now, setNow] = useState(Date.now());
  const isLowBalanceRef = useRef(false);

  const lastShownTime = useLowCroBalanceDrawer((s) => s.lastShownTime);

  isLowBalanceRef.current =
    typeof balanceRet.data !== 'undefined' && balanceRet.data < MIN_CRO;
  const isInStakeView = realPathname === pathDict.cro.stake;

  if (
    isInStakeView &&
    isConnected &&
    isConnectedToSupportedChain &&
    !isShownRef.current &&
    !timerRef.current &&
    (!lastShownTime || now - lastShownTime >= SHOW_AGAIN_MICROSECONDS) &&
    isLowBalanceRef.current
  ) {
    timerRef.current = setTimeout(() => {
      // if transak modal is open or balance not low, no need to popup
      if (useTransakModal.getState().isOpen || !isLowBalanceRef.current) {
        timerRef.current = undefined;
        return;
      }

      isShownRef.current = true;

      useLowCroBalanceDrawer.setState({
        lastShownTime: Date.now(),
      });

      disclosure.onOpen();

      timerRef.current = undefined;
    }, WAIT_MICROSECONDS);
  }

  useInterval(() => {
    if (!isInStakeView || isShownRef.current || !isLowBalanceRef.current) {
      return;
    }
    setNow(Date.now());
  }, 1_000);

  useUnmount(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  });

  return disclosure;
};
